import { useState } from "react";
import axios from "axios.js";

// react-router-dom components
import { Link, useNavigate, useLocation } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
// import Grid from "@mui/material/Grid";
// import MuiLink from "@mui/material/Link";

// @mui icons
// import FacebookIcon from "@mui/icons-material/Facebook";
// import GitHubIcon from "@mui/icons-material/GitHub";
// import GoogleIcon from "@mui/icons-material/Google";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
// import MDInput from "components/MDInput";
import MDButton from "theme/components/MDButton";

// Authentication layout components
import BasicLayout from "app/user/components/BasicLayout";

// Images
import bgImage from "assets/images/bg-sign-in-basic.jpeg";

import { useSignIn, useIsAuthenticated } from "react-auth-kit";
import { Formik } from "formik";
import * as Yup from "yup";
import { Snackbar, TextField, Alert, Backdrop, CircularProgress } from "@mui/material";
// import { LoadingButton } from "@mui/lab";
import usePageTitle from "app/pageTitle";

// inital login credentials
const initialValues = {
  email: "",
  password: "",
  remember: true,
};

// form field validation schema
const validationSchema = Yup.object().shape({
  password: Yup.string()
    .min(6, "Password must be 6 character length")
    .required("Password is required!"),
  email: Yup.string().email("Invalid Email address").required("Email is required!"),
});

function UserSignin() {
  const title = "Sign In";
  usePageTitle(title);

  const location = useLocation();
  const redirectLocation = location.state?.from || "/dashboard";

  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  if (isAuthenticated()) {
    navigate("/dashboard");
  }

  const signIn = useSignIn();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  function emptyCache() {
    if ("caches" in window) {
      caches.keys().then((names) => {
        // Delete all the cache files
        names.forEach((name) => {
          caches.delete(name);
        });
      });

      // Makes sure the page reloads. Changes are only visible after you refresh.
      // window.location.reload(true);
    }
  }

  function versionRefresh() {
    emptyCache();
    window.location.replace(redirectLocation);
    // const cacheWhitelist = ["alero-pwa"];
    // caches.keys().then((cacheNames) =>
    //   cacheNames.map((cacheName) => {
    //     if (cacheWhitelist.indexOf(cacheName) === -1) {
    //       return caches.delete(cacheName);
    //     }
    //     return null;
    //   })
    // );
  }

  const handleFormSubmit = async (values) => {
    setShowLoading(true);
    try {
      const json = { email_address: values.email, password: values.password };
      const response = await axios.post("users/authenticate.json", json);
      if (response.data.status === "error") {
        setOpen(true);
        setShowLoading(false);
        setErrMsg(response.data.data.message);
        setSeverity("error");
      } else {
        // Set token header globally
        axios.defaults.headers.common.token = response.data.data.token.token;
        localStorage.setItem("_al_auth_token", response.data.data.token.token);

        // Handle App Update
        const currentVersion = localStorage.getItem("_al_version_");
        const newVersion = response.data.version;
        let shouldUpdate = false;
        if (currentVersion !== newVersion) {
          shouldUpdate = true;
        }
        localStorage.setItem("_al_version_", newVersion);

        const signedIn = signIn({
          token: response.data.data.token.token,
          expiresIn: response.data.data.token.duration / 60,
          // expiresIn: 120,
          tokenType: "token",
          authState: response.data.data.user,
          // refreshToken: res.data.refreshToken, // Only if you are using refreshToken feature
          // refreshTokenExpireIn: res.data.refreshTokenExpireIn, // Only if you are using refreshToken feature
        });
        if (signedIn) {
          setOpen(true);
          setShowLoading(false);
          setErrMsg("Sign in success!");
          setSeverity("success");

          if (shouldUpdate) {
            if (
              window.confirm(
                "A new version is available. Alero will automatically refresh to get the latest update."
              ) === true
            ) {
              versionRefresh();
            } else {
              versionRefresh();
            }
          } else {
            navigate(redirectLocation, { replace: true });
          }
        } else {
          // Throw error
          setOpen(true);
          setShowLoading(false);
          setErrMsg("Unable to sign you in.");
          setSeverity("error");
        }
      }
    } catch (e) {
      setShowLoading(false);
      setErrMsg("An error occurred. Please try again later.");
      setSeverity("error");
      setOpen(true);
    }
  };

  return (
    <BasicLayout image={bgImage}>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="warning"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Alero Africa
          </MDTypography>
          <MDTypography display="block" variant="button" color="white" my={1}>
            Sign in to your dashboard
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox role="form">
            <Formik
              onSubmit={handleFormSubmit}
              initialValues={initialValues}
              validationSchema={validationSchema}
            >
              {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                  <MDBox mb={2}>
                    {/* <MDInput type="email" label="Email" fullWidth /> */}
                    <TextField
                      fullWidth
                      size="small"
                      type="email"
                      name="email"
                      label="Email"
                      variant="outlined"
                      onBlur={handleBlur}
                      value={values.email}
                      onChange={handleChange}
                      helperText={touched.email && errors.email}
                      error={Boolean(errors.email && touched.email)}
                      sx={{ mb: 3 }}
                    />
                  </MDBox>
                  <MDBox mb={2}>
                    {/* <MDInput type="password" label="Password" fullWidth /> */}
                    <TextField
                      fullWidth
                      size="small"
                      name="password"
                      type="password"
                      label="Password"
                      variant="outlined"
                      onBlur={handleBlur}
                      value={values.password}
                      onChange={handleChange}
                      helperText={touched.password && errors.password}
                      error={Boolean(errors.password && touched.password)}
                      sx={{ mb: 1.5 }}
                    />
                  </MDBox>
                  <MDBox mt={4} mb={1}>
                    <MDButton variant="gradient" color="warning" type="submit" fullWidth>
                      sign in
                    </MDButton>
                    {/* <LoadingButton
                      fullWidth
                      type="submit"
                      color="primary"
                      loading={loading}
                      variant="contained"
                      sx={{ my: 2 }}
                    >
                      <div sx={{ color: "white" }}>Login</div>
                    </LoadingButton> */}
                  </MDBox>
                </form>
              )}
            </Formik>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Don&apos;t have an account?{" "}
                <MDTypography
                  component={Link}
                  to="/sign-up"
                  variant="button"
                  color="warning"
                  fontWeight="medium"
                  textGradient
                >
                  Sign up
                </MDTypography>
              </MDTypography>
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                <MDTypography
                  component={Link}
                  to="/forgot-password"
                  variant="button"
                  color="warning"
                  fontWeight="medium"
                  textGradient
                >
                  Forgot password?
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default UserSignin;
